import { Layout } from "antd";
import { flagConstants } from "constants/index";
import { StoreContext } from "contexts";
import { flagHooks, trackingHooks, userHooks } from "hooks";
import { IRoute } from "models";
import React, { lazy, Suspense, useContext, useEffect } from "react";
import { useLocation } from "react-router-dom";
import routes from "routes";
import { iframeUtils } from "utils";
import AppContent from "./AppContent";
import AppHeader from "./AppHeader";
import "./AppLayout.scss";
import AppSider from "./AppSider";

const { getDataFromIframe } = iframeUtils;
const { useTrackEventsFromParent } = trackingHooks;
const { FLAG_KEYS } = flagConstants;

/**
 * OMNI-TIC2026 (T49) — nạp ĐỘNG, và đó là nửa thứ hai của việc cắm cờ.
 *
 * Import tĩnh thì code của launcher nằm trong chunk chính, tức nó **được tải kể cả khi cờ tắt** —
 * chỉ là không render. `lazy` + cổng cờ ở dưới làm đúng điều review yêu cầu: cờ tắt ⇒ **không tải
 * thêm gì mới lên**, không chỉ là không hiện.
 */
const SupportLauncherMount = lazy(() => import("./SupportLauncherMount"));

const AppLayout: React.FC = () => {
  const { filteredNavigation: filteredNavigationByPermissions } = userHooks.useAuthorizationData(routes as IRoute[]);

  const { filteredNavigation, filteredRoutes } = flagHooks.useFilterNavigationByFeatureFlags(
    filteredNavigationByPermissions
  );

  const { inIframe, featureFlagsData } = useContext(StoreContext);

  const location = useLocation();
  useEffect(() => {
    if (inIframe) {
      const parentWindow = window.parent;
      const { parentOrigin = "" } = getDataFromIframe();
      const { search, pathname } = window.location;
      parentWindow.postMessage(`pathname=${pathname}${search}`, parentOrigin);
      parentWindow.postMessage("component-rendered", parentOrigin); // https://confluence.teko.vn/display/HCM9/OMPRODUCT-545%3A+Tracking+component+GiveFeedback
    }
  }, [location, inIframe]);

  useTrackEventsFromParent();

  return (
    <Layout className="app-layout">
      {!inIframe && <AppSider filteredNavigation={filteredNavigation} />}
      <Layout>
        <AppHeader filteredRoutes={filteredRoutes} />
        <AppContent filteredRoutes={filteredRoutes} />
      </Layout>
      {/* OMNI-TIC2026 (T49) — launcher hỗ trợ nổi, và CỔNG CỜ NẰM Ở ĐÂY, ngoài component.

          Đặt ở đây chứ không chỉ bên trong component vì hai lý do khác nhau:
            1. cờ tắt ⇒ không render VÀ không tải chunk (xem `lazy` ở trên) — "không load thêm gì mới";
            2. đọc `AppLayout` là thấy ngay epic này bị gác bằng cờ nào, không phải mở component mới biết.

          FAIL CLOSED: `?.enabled === true` ⇒ cờ thiếu / chưa đăng ký / lỗi tải flags đều ra false.
          Khác cố ý với bộ lọc route dùng chung (`utils/flagsup.ts`), chỗ đó coi key VẮNG là hiện —
          mặc định đó ổn cho feature đã ship, nhưng sai cho một pilot chưa mở.

          Component vẫn giữ cổng riêng của nó (permission + iframe + 3 module pilot). Đó không phải
          trùng lặp: ở đây quyết định CÓ NẠP KHÔNG, trong đó quyết định CÓ HIỆN KHÔNG. */}
      {featureFlagsData[FLAG_KEYS.SUPPORT_ASSISTANT_WORKSPACE]?.enabled === true && (
        <Suspense fallback={null}>
          <SupportLauncherMount routes={filteredRoutes} />
        </Suspense>
      )}
    </Layout>
  );
};

export default AppLayout;
